import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useDealDetail = (dealId, getAuthHeaders) => {
  const [deal, setDeal] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [broker, setBroker] = useState(null);
  const [funder, setFunder] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsSummary, setTransactionsSummary] = useState(null);
  const [syndications, setSyndications] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [refinanceInfo, setRefinanceInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDealDetails = useCallback(async () => {
    if (!dealId) return;
    
    // Reset loading state when fetching a new deal (critical for prev/next navigation)
    setLoading(true);
    // Reset the per-deal contact slots so a previous deal's merchant/broker/funder doesn't
    // bleed into the new deal page when the new deal has a missing or different link.
    setMerchant(null);
    setBroker(null);
    setFunder(null);
    setRefinanceInfo(null);

    try {
      // First fetch the deal to get the UUID
      const dealRes = await axios.get(`${API}/deals/${dealId}`, { headers: getAuthHeaders() });
      const dealData = dealRes.data;
      const dealUuid = dealData.id;  // Use the UUID for related queries

      // Fire ALL secondary requests in parallel. Previously merchant / broker /
      // funder / refinance-info were chained sequentially after the Promise.all,
      // adding ~4 × 100-200ms of ingress overhead per deal navigation. Folding
      // them into a single Promise.all collapses that into one round-trip slot.
      const [
        transactionsRes, syndicationsRes, documentsRes, contactsRes, usersRes,
        merchantRes, brokerRes, funderRes, refiRes,
      ] = await Promise.all([
        axios.get(`${API}/transactions?deal_id=${dealUuid}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/syndications?deal_id=${dealUuid}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deals/${dealId}/documents`, { headers: getAuthHeaders() }),
        axios.get(`${API}/contacts?type=Investor`, { headers: getAuthHeaders() }),
        axios.get(`${API}/users`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        dealData.company_id
          ? axios.get(`${API}/contacts/${dealData.company_id}`, { headers: getAuthHeaders() }).catch(() => null)
          : Promise.resolve(null),
        dealData.broker_id
          ? axios.get(`${API}/contacts/${dealData.broker_id}`, { headers: getAuthHeaders() }).catch(() => null)
          : Promise.resolve(null),
        dealData.funder_id
          ? axios.get(`${API}/contacts/${dealData.funder_id}`, { headers: getAuthHeaders() }).catch(() => null)
          : Promise.resolve(null),
        axios.get(`${API}/deals/${dealId}/refinance-info`, { headers: getAuthHeaders() }).catch(() => null),
      ]);

      setDeal(dealData);
      // Handle new response format: {transactions: [...], summary: {...}}
      const txData = transactionsRes.data;
      if (txData && txData.transactions) {
        setTransactions(txData.transactions);
        setTransactionsSummary(txData.summary || null);
      } else if (Array.isArray(txData)) {
        setTransactions(txData);
        setTransactionsSummary(null);
      } else {
        setTransactions([]);
        setTransactionsSummary(null);
      }
      setSyndications(syndicationsRes.data);
      setDocuments(documentsRes.data);
      
      // Combine investor contacts AND syndicator users
      const investorContacts = contactsRes.data.map(c => ({
        id: c.id,
        company_name: c.company_name,
        type: 'contact'
      }));
      
      const syndicatorUsers = (usersRes.data || [])
        .filter(u => u.roles?.includes('syndicator'))
        .map(u => ({
          id: u.id,
          company_name: `${u.name} (User)`,
          type: 'user'
        }));
      
      // Combine and deduplicate
      const combined = [...investorContacts];
      syndicatorUsers.forEach(su => {
        if (!combined.find(c => c.id === su.id)) {
          combined.push(su);
        }
      });
      
      setInvestors(combined);

      setMerchant(merchantRes ? merchantRes.data : null);
      setBroker(brokerRes ? brokerRes.data : null);
      setFunder(funderRes ? funderRes.data : null);
      setRefinanceInfo(refiRes ? refiRes.data : null);
    } catch (error) {
      console.error('Failed to fetch deal details:', error);
      toast.error('Failed to load deal details');
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuthHeaders]);

  // Lightweight refetch for transaction changes - only fetches deal and transactions
  const refetchTransactions = useCallback(async () => {
    if (!dealId) return;
    
    try {
      const dealRes = await axios.get(`${API}/deals/${dealId}`, { headers: getAuthHeaders() });
      const dealData = dealRes.data;
      const dealUuid = dealData.id;
      
      const transactionsRes = await axios.get(`${API}/transactions?deal_id=${dealUuid}`, { headers: getAuthHeaders() });
      
      setDeal(dealData);
      const txData = transactionsRes.data;
      if (txData && txData.transactions) {
        setTransactions(txData.transactions);
        setTransactionsSummary(txData.summary || null);
      } else if (Array.isArray(txData)) {
        setTransactions(txData);
        setTransactionsSummary(null);
      } else {
        setTransactions([]);
        setTransactionsSummary(null);
      }

      // Re-fetch refinance info too — deleting / moving / editing a
      // REFINANCE_PAYOFF transaction triggers the system-wide refi-unlink rule
      // on the backend, which clears refi pointers on the deal. Without this
      // refetch the "Refinanced into / from" banner would render stale data
      // (this was the deal-#10408 → #10455 case: the link cleared in DB but
      // the banner persisted until a hard reload).
      try {
        const refiRes = await axios.get(`${API}/deals/${dealId}/refinance-info`, { headers: getAuthHeaders() });
        setRefinanceInfo(refiRes.data);
      } catch (err) {
        // 404 / no-link is fine — clear local state so the banner hides
        setRefinanceInfo(null);
      }
    } catch (error) {
      console.error('Failed to refetch transactions:', error);
    }
  }, [dealId, getAuthHeaders]);

  useEffect(() => {
    fetchDealDetails();
  }, [fetchDealDetails]);

  return {
    deal,
    setDeal,
    merchant,
    broker,
    funder,
    transactions,
    transactionsSummary,
    syndications,
    documents,
    investors,
    refinanceInfo,
    loading,
    refetch: fetchDealDetails,
    refetchTransactions,
  };
};

export default useDealDetail;
